import React from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline,
  HighlightOff,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from 'src/common/hooks';
import { CommitmentReadinessYearSchema } from 'src/Ghg/interfaces';
import { useAppDispatch } from 'src/redux-file/hooks';
import { setSelectedDate } from 'src/redux-file/thunk';
import {
  formatStatementDate,
  resolveStatementLanguage,
} from './statementFormat';

const DIRECT_EMISSIONS_PATH =
  '/ghg/emissions/questionnairepage-direct-emissions';
const ELECTRICITY_EMISSIONS_PATH =
  '/ghg/emissions/questionnairepage-electricity-emissions';

// RG 28.08 2.3: the reference year the Scope 1/2 rows are about. Both scopes
// must be complete for the SAME year, so the rows never mix two years.
// Raben live review 2026-10-02: the suggestion is always the last reporting
// year (the year before the current one) -- another year only when that one
// is not eligible but an earlier one is, or when the server did not send it.
// Then: the latest eligible year, otherwise the year closest to eligible
// (highest min(scope 1, scope 2), then scope 1 + scope 2, then the latest
// year). `undefined` only when the server sent no years at all.
export function pickReferenceYear(
  years: CommitmentReadinessYearSchema[],
  eligibleYears: number[],
  today: Date = new Date()
): number | undefined {
  const lastReportingYear = today.getFullYear() - 1;
  if (eligibleYears.includes(lastReportingYear)) {
    return lastReportingYear;
  }
  if (eligibleYears.length > 0) {
    return Math.max(...eligibleYears);
  }
  if (years.some((row) => row.year === lastReportingYear)) {
    return lastReportingYear;
  }
  const ranked = [...years].sort(
    (a, b) =>
      Math.min(b.scope_1_percent, b.scope_2_percent) -
        Math.min(a.scope_1_percent, a.scope_2_percent) ||
      b.scope_1_percent +
        b.scope_2_percent -
        (a.scope_1_percent + a.scope_2_percent) ||
      b.year - a.year
  );
  return ranked[0]?.year;
}

// Never shows "100%" for a scope that is not complete (99.6 would round up).
const displayPercent = (value: number) => {
  const pct = Number(value) || 0;
  if (pct >= 100) return 100;
  return Math.min(99, Math.max(0, Math.round(pct)));
};

// Below the `sm` breakpoint the table turns into a stacked list: each cell
// carries its column title in `data-label`, the header row stays only for
// screen readers.
const tableSx = {
  '& .MuiTableCell-root': { verticalAlign: 'top' },
  '& .MuiTableCell-head': { fontWeight: 600 },
  '@media (max-width: 599.95px)': {
    '& thead': {
      position: 'absolute',
      width: '1px',
      height: '1px',
      overflow: 'hidden',
      clip: 'rect(0 0 0 0)',
    },
    '& tbody, & tr, & td': { display: 'block' },
    '& tr': { borderBottom: '1px solid #dddddd', py: 1 },
    '& td': { borderBottom: 'none', py: 0.5, px: 1 },
    '& td[data-empty="true"]': { display: 'none' },
    '& td::before': {
      content: 'attr(data-label)',
      display: 'block',
      fontSize: '0.75rem',
      fontWeight: 600,
    },
  },
};

type StatusKind = 'done' | 'missing' | 'pending';

function Status({
  kind,
  children,
}: {
  kind: StatusKind;
  children: React.ReactNode;
}) {
  const sx = { fontSize: 20 };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {kind === 'done' && (
        <CheckCircleOutline sx={{ ...sx, color: 'success.main' }} />
      )}
      {kind === 'missing' && (
        <HighlightOff sx={{ ...sx, color: 'error.main' }} />
      )}
      {kind === 'pending' && <RadioButtonUnchecked sx={sx} />}
      <Typography variant="body2" component="span">
        {children}
      </Typography>
    </Box>
  );
}

interface LetterRowProps {
  consent: boolean | null;
  acceptedAt?: string | null;
  canDecide: boolean;
}

function LetterRow({ consent, acceptedAt, canDecide }: LetterRowProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const P = 'ghg.targetReadiness';

  const acceptedOn = acceptedAt
    ? formatStatementDate(resolveStatementLanguage(language), acceptedAt)
    : '';

  let status: React.ReactNode;
  if (consent === true) {
    status = (
      <Status kind="done">
        {String(t(`${P}.statusAccepted`, { date: acceptedOn })).trim()}
      </Status>
    );
  } else if (consent === false) {
    status = <Status kind="missing">{t(`${P}.statusDeclined`)}</Status>;
  } else {
    status = <Status kind="pending">{t(`${P}.statusPending`)}</Status>;
  }

  let note: React.ReactNode = null;
  if (consent !== true) {
    if (canDecide) {
      note = (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t(
              consent === false
                ? `${P}.noteLetterDeclined`
                : `${P}.noteLetterPending`
            )}
          </Typography>
          {/* The Letter section sits at the top of the page; the page
              scrolls on the window (ScrollToLocation does the same). */}
          <Button
            variant="outlined"
            size="small"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            {t(`${P}.goToLetter`)}
          </Button>
        </>
      );
    } else {
      note = (
        <Typography variant="body2">{t(`${P}.noteLetterReadOnly`)}</Typography>
      );
    }
  }

  return (
    <TableRow data-testid="readiness-row-letter">
      <TableCell data-label={t(`${P}.columnStep`)}>
        {t(`${P}.stepLetter`)}
      </TableCell>
      <TableCell data-label={t(`${P}.columnStatus`)}>{status}</TableCell>
      <TableCell
        data-label={t(`${P}.columnNote`)}
        data-empty={note === null ? 'true' : undefined}
      >
        {note}
      </TableCell>
    </TableRow>
  );
}

interface YearRowsProps {
  year: number;
  scope1Percent: number;
  scope2Percent: number;
  scope1HasData: boolean;
}

// The two scope rows, both about the same year. Raben live review 2026-10-02:
// no Supplier Survey row any more -- a carrier on this tab has been through
// the survey already, and the survey link led back out through the login.
// Scope 1 is done only when complete AND holding data (emissions above zero):
// every "data entry completed" box ticked over empty forms is not data.
function YearRows({
  year,
  scope1Percent,
  scope2Percent,
  scope1HasData,
}: YearRowsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const P = 'ghg.targetReadiness';

  // The year is set in the store BEFORE navigating. Going through
  // `?supplier_survey_year=` would race with Scope12UnlockGuard, which can
  // redirect to the dashboard before the new year is in effect.
  const goToScope = async (path: string) => {
    await dispatch(setSelectedDate(new Date(year, 0, 1)));
    navigate(path);
  };

  const scopeRows = [
    {
      testId: 'readiness-row-scope1',
      scopeTitle: String(t('direct-emission.title')).trim(),
      percent: scope1Percent,
      hasData: scope1HasData,
      path: DIRECT_EMISSIONS_PATH,
      goTo: `${P}.goToScope1`,
    },
    {
      testId: 'readiness-row-scope2',
      scopeTitle: String(t('electricity-emission.title')).trim(),
      percent: scope2Percent,
      hasData: true,
      path: ELECTRICITY_EMISSIONS_PATH,
      goTo: `${P}.goToScope2`,
    },
  ];

  return (
    <>
      {scopeRows.map((row) => {
        const marked = row.percent >= 100;
        const complete = marked && row.hasData;
        const markedWithoutData = marked && !row.hasData;
        let status: React.ReactNode;
        if (complete) {
          status = <Status kind="done">{t(`${P}.statusComplete`)}</Status>;
        } else if (markedWithoutData) {
          status = <Status kind="missing">{t(`${P}.statusNoData`)}</Status>;
        } else {
          status = (
            <Status kind="pending">
              {t(`${P}.statusPercent`, { pct: displayPercent(row.percent) })}
            </Status>
          );
        }
        return (
          <TableRow key={row.testId} data-testid={row.testId}>
            <TableCell data-label={t(`${P}.columnStep`)}>
              {t(`${P}.stepScope`, { scope: row.scopeTitle, year })}
            </TableCell>
            <TableCell data-label={t(`${P}.columnStatus`)}>{status}</TableCell>
            <TableCell
              data-label={t(`${P}.columnNote`)}
              data-empty={complete ? 'true' : undefined}
            >
              {!complete && (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {markedWithoutData
                      ? t(`${P}.noteScope1NoData`, { year })
                      : t(`${P}.noteScope`, {
                          checkbox: t(
                            'ghg.dataEntryCompletedCheckbox.labelNotCompleted'
                          ),
                          year,
                        })}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => goToScope(row.path)}
                  >
                    {t(row.goTo)}
                  </Button>
                </>
              )}
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
}

interface Props {
  consent: boolean | null;
  acceptedAt?: string | null;
  // The carrier's own users may decide about the Letter (D2); everybody else
  // only sees its state, so no "go and accept it" hint for them.
  canDecide: boolean;
  years: CommitmentReadinessYearSchema[];
  eligibleYears: number[];
}

// RG 28.08 2.3: "Required step | Your status | What to do". Rendered by
// TargetArea until the target is set, in every Letter state. Takes the
// readiness data as props (TargetArea owns the query) and makes no requests
// of its own.
export default function TargetReadinessChecklist({
  consent,
  acceptedAt,
  canDecide,
  years,
  eligibleYears,
}: Props) {
  const { t } = useTranslation();
  const P = 'ghg.targetReadiness';

  const referenceYear = pickReferenceYear(years, eligibleYears);
  const referenceRow = years.find((row) => row.year === referenceYear);

  return (
    <Box data-testid="target-readiness-checklist" sx={{ mb: 3 }}>
      <Table
        size="small"
        aria-label={String(t(`${P}.tableLabel`))}
        sx={tableSx}
      >
        <TableHead>
          <TableRow>
            <TableCell>{t(`${P}.columnStep`)}</TableCell>
            <TableCell>{t(`${P}.columnStatus`)}</TableCell>
            <TableCell>{t(`${P}.columnNote`)}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <LetterRow
            consent={consent}
            acceptedAt={acceptedAt}
            canDecide={canDecide}
          />
          {referenceRow && (
            <YearRows
              year={referenceRow.year}
              scope1Percent={referenceRow.scope_1_percent}
              scope2Percent={referenceRow.scope_2_percent}
              // An older server without the field keeps today's behaviour.
              scope1HasData={referenceRow.scope_1_has_data !== false}
            />
          )}
        </TableBody>
      </Table>
      {years.length > 0 && (
        <Box sx={{ mt: 2 }} data-testid="readiness-footer">
          <Typography variant="body2">
            {t(`${P}.footerYears`, {
              years: years.map((row) => row.year).join(', '),
            })}
          </Typography>
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
            {years.map((row) => (
              <Typography key={row.year} component="li" variant="body2">
                {t(`${P}.yearSummary`, {
                  year: row.year,
                  scope1: displayPercent(row.scope_1_percent),
                  scope2: displayPercent(row.scope_2_percent),
                })}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
